import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, P, UL, H3 } from "@/components/marketing/LegalPage";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/vi/privacy")({
  head: () => ({
    meta: [
      { title: "Chính sách Bảo mật — banana.cards" },
      { name: "description", content: "Cách Banana Cards thu thập, sử dụng, lưu trữ và bảo vệ dữ liệu cá nhân của bạn." },
      { property: "og:title", content: "Chính sách Bảo mật — banana.cards" },
      { property: "og:description", content: "Đọc Chính sách Bảo mật của Banana Cards." },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/privacy", "vi"),
  }),
  component: PrivacyPageVi,
});

function PrivacyPageVi() {
  return (
    <LegalPage
      eyebrow="Pháp lý"
      title="Chính sách"
      italic="Bảo mật."
      updated="Tháng 11, 2025"
      intro="Tại Banana Cards, chúng tôi cam kết bảo vệ dữ liệu cá nhân và tôn trọng quyền riêng tư của bạn. Chính sách này giải thích cách chúng tôi thu thập, sử dụng, tiết lộ, lưu trữ và bảo vệ thông tin của bạn khi bạn sử dụng các sản phẩm và dịch vụ của chúng tôi."
      related={[
        { label: "Điều khoản Dịch vụ", to: "/vi/terms" },
        { label: "Hướng dẫn Pháp lý", to: "/vi/legal-guidelines" },
        { label: "Liên hệ với chúng tôi", to: "/vi/contact" },
      ]}
      sections={[
        {
          id: "who",
          title: "Chúng tôi là ai & Cách liên hệ",
          body: (
            <>
              <P>Six Studio Creative FZCO là đơn vị kiểm soát dữ liệu chịu trách nhiệm về thông tin cá nhân của bạn.</P>
              <P>Nếu bạn có bất kỳ câu hỏi hoặc lo ngại nào về Chính sách Bảo mật này hoặc cách chúng tôi xử lý dữ liệu của bạn, vui lòng liên hệ <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>.</P>
            </>
          ),
        },
        {
          id: "info",
          title: "Thông tin chúng tôi thu thập",
          body: (
            <>
              <H3>Thông tin bạn cung cấp trực tiếp</H3>
              <UL items={[
                "Họ và tên",
                "Địa chỉ email",
                "Số điện thoại",
                "Tên công ty, chức danh, và ngành nghề",
                "Địa chỉ thanh toán và giao hàng",
                "Thông tin đăng nhập tài khoản",
                "Nội dung hồ sơ bạn chọn công khai (tiểu sử, liên kết, hình ảnh, video)",
                "Trao đổi với chúng tôi (hỗ trợ, phản hồi, tin nhắn)",
              ]} />
              <H3>Thông tin Thanh toán</H3>
              <P>Thanh toán được xử lý bởi các bên cung cấp dịch vụ thanh toán thứ ba. Chúng tôi không lưu trữ chi tiết đầy đủ thẻ thanh toán. Chúng tôi có thể nhận các tham chiếu giao dịch giới hạn cần thiết cho kế toán và hỗ trợ.</P>
              <H3>Thông tin thu thập tự động</H3>
              <UL items={[
                "Địa chỉ IP",
                "Loại thiết bị, trình duyệt và hệ điều hành",
                "Dữ liệu sử dụng (trang đã truy cập, tính năng đã dùng, dấu thời gian tương tác)",
                "Nguồn giới thiệu",
                "Dữ liệu cookie và theo dõi",
              ]} />
              <H3>Dữ liệu Bên thứ ba & Quy kết</H3>
              <P>Nếu bạn tương tác với Banana Cards qua liên kết affiliate, mã QR hoặc thẻ NFC, chúng tôi có thể thu thập dữ liệu quy kết và tương tác để đảm bảo chức năng, phân tích và theo dõi hoa hồng chính xác.</P>
              <H3>Dữ liệu Trẻ em</H3>
              <P>Dịch vụ Banana Cards không dành cho trẻ em và chúng tôi không cố ý thu thập dữ liệu cá nhân từ cá nhân dưới độ tuổi được luật áp dụng yêu cầu.</P>
            </>
          ),
        },
        {
          id: "legal-basis",
          title: "Cơ sở Pháp lý để Xử lý",
          body: (
            <>
              <P>Chúng tôi xử lý dữ liệu cá nhân của bạn dựa trên một hoặc nhiều căn cứ pháp lý sau:</P>
              <UL items={[
                "Cần thiết theo hợp đồng — để cung cấp các sản phẩm và dịch vụ bạn yêu cầu",
                "Sự đồng ý — khi bạn đã đồng ý rõ ràng (ví dụ: truyền thông tiếp thị)",
                "Nghĩa vụ pháp lý — bao gồm yêu cầu kế toán, thuế và quy định",
                "Lợi ích hợp pháp — cải thiện dịch vụ, bảo mật, phân tích, ngăn chặn gian lận",
              ]} />
            </>
          ),
        },
        {
          id: "use",
          title: "Cách chúng tôi sử dụng thông tin của bạn",
          body: (
            <UL items={[
              "Tạo và quản lý tài khoản Banana Cards của bạn",
              "Cung cấp, vận hành và cải thiện sản phẩm và dịch vụ",
              "Xử lý đơn hàng, giao hàng, kích hoạt và thanh toán",
              "Kích hoạt NFC, QR, chia sẻ hồ sơ, CRM và phân tích",
              "Trao đổi với bạn về hỗ trợ, cập nhật hoặc thông báo dịch vụ",
              "Gửi thông tin tiếp thị khi được phép",
              "Duy trì bảo mật nền tảng và ngăn chặn lạm dụng",
              "Tuân thủ nghĩa vụ pháp lý và quy định",
            ]} />
          ),
        },
        {
          id: "sharing",
          title: "Cách chúng tôi chia sẻ thông tin của bạn",
          body: (
            <>
              <P><strong>Chúng tôi không bán dữ liệu cá nhân của bạn.</strong></P>
              <P>Chúng tôi có thể chia sẻ thông tin cá nhân giới hạn chỉ khi cần thiết, bao gồm với:</P>
              <UL items={[
                "Nhà cung cấp dịch vụ (lưu trữ, xử lý thanh toán, phân tích, gửi email)",
                "Đối tác logistics và giao hàng cho phần cứng",
                "Nền tảng tiếp thị và quảng cáo ở dạng tổng hợp hoặc giới hạn",
                "Cố vấn chuyên môn (pháp lý, kế toán, tuân thủ)",
                "Cơ quan quản lý hoặc chức năng khi luật pháp yêu cầu",
                "Chuyển giao doanh nghiệp (sáp nhập, mua lại, bán tài sản)",
              ]} />
              <P>Chúng tôi cam kết không bán, chia sẻ hoặc trao đổi thông tin cá nhân của khách hàng với bất kỳ bên thứ ba nào vì mục đích thương mại, trừ khi luật pháp yêu cầu hoặc phục vụ mục đích giao hàng (chia sẻ địa chỉ/số điện thoại với đơn vị vận chuyển).</P>
            </>
          ),
        },
        {
          id: "cookies",
          title: "Cookies & Công nghệ Theo dõi",
          body: (
            <>
              <P>Chúng tôi sử dụng cookies và các công nghệ tương tự để:</P>
              <UL items={[
                "Đảm bảo chức năng và bảo mật website",
                "Ghi nhớ tùy chọn người dùng",
                "Phân tích hiệu suất và mức độ sử dụng",
                "Cung cấp tiếp thị phù hợp (khi được phép)",
              ]} />
              <P>Bạn có thể quản lý tùy chọn cookie qua trình duyệt. Tắt cookie có thể ảnh hưởng đến một số tính năng.</P>
            </>
          ),
        },
        {
          id: "transfers",
          title: "Truyền dữ liệu Quốc tế",
          body: (
            <>
              <P>Thông tin của bạn có thể được xử lý hoặc lưu trữ bên ngoài quốc gia cư trú, bao gồm cả các khu vực pháp lý nơi luật bảo vệ dữ liệu có thể khác biệt.</P>
              <P>Khi cần, chúng tôi bảo vệ việc truyền dữ liệu quốc tế bằng các cơ chế pháp lý phù hợp, như Điều khoản Hợp đồng Tiêu chuẩn hoặc bảo vệ tương đương.</P>
            </>
          ),
        },
        {
          id: "marketing",
          title: "Truyền thông Tiếp thị",
          body: (
            <>
              <P>Chúng tôi có thể liên hệ với bạn qua email hoặc các kênh khác về cập nhật sản phẩm, ưu đãi hoặc thông tin liên quan khi được luật pháp cho phép.</P>
              <P>Bạn có thể hủy đăng ký bất kỳ lúc nào bằng cách nhấp vào liên kết hủy đăng ký trong email hoặc liên hệ <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>.</P>
            </>
          ),
        },
        {
          id: "rights",
          title: "Quyền Bảo vệ Dữ liệu của bạn",
          body: (
            <>
              <P>Tùy vào vị trí của bạn, bạn có thể có quyền:</P>
              <UL items={[
                "Truy cập dữ liệu cá nhân của bạn",
                "Sửa dữ liệu không chính xác hoặc không đầy đủ",
                "Yêu cầu xóa dữ liệu của bạn",
                "Hạn chế hoặc phản đối xử lý",
                "Rút lại sự đồng ý bất kỳ lúc nào",
                "Yêu cầu tính di động dữ liệu",
                "Khiếu nại với cơ quan bảo vệ dữ liệu",
              ]} />
              <P>Để thực hiện quyền của bạn, liên hệ <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a> với đủ thông tin để xác minh danh tính.</P>
            </>
          ),
        },
        {
          id: "security",
          title: "Bảo mật & Lưu trữ Dữ liệu",
          body: (
            <>
              <P>Chúng tôi áp dụng các biện pháp kỹ thuật và tổ chức phù hợp để bảo vệ dữ liệu của bạn khỏi truy cập trái phép, mất mát hoặc lạm dụng.</P>
              <P>Chúng tôi chỉ lưu trữ dữ liệu cá nhân trong thời gian cần thiết cho các mục đích nêu trong chính sách này hoặc theo yêu cầu của luật pháp.</P>
            </>
          ),
        },
        {
          id: "google-api",
          title: "Chính sách Dữ liệu Người dùng Dịch vụ Google API",
          body: (
            <>
              <P>Việc sử dụng và truyền thông tin nhận được từ Google APIs của banana-cards-calendar-sync sang bất kỳ ứng dụng nào khác sẽ tuân thủ <a href="https://developers.google.com/terms/api-services-user-data-policy" className="font-bold underline-offset-4 hover:underline" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, bao gồm các yêu cầu Sử dụng Giới hạn.</P>
              <H3>Sử dụng Giới hạn</H3>
              <P>Ứng dụng của chúng tôi tuân thủ nghiêm ngặt Chính sách Dữ liệu Người dùng Dịch vụ Google API. Chúng tôi chỉ sử dụng dữ liệu Google Workspace của bạn để cung cấp hoặc cải thiện các tính năng hướng đến người dùng nổi bật trong giao diện. Chúng tôi không bán dữ liệu, không sử dụng cho quảng cáo, và không cho phép con người đọc dữ liệu trừ khi được bạn cho phép rõ ràng vì lý do hỗ trợ hoặc bảo mật.</P>
            </>
          ),
        },
        {
          id: "changes",
          title: "Thay đổi Chính sách này",
          body: (
            <>
              <P>Chúng tôi có thể cập nhật Chính sách Bảo mật này theo thời gian. Phiên bản mới nhất luôn có sẵn trên website của chúng tôi, với ngày cập nhật ở đầu trang.</P>
              <P>Việc tiếp tục sử dụng dịch vụ sau khi các thay đổi có hiệu lực đồng nghĩa với việc chấp nhận chính sách đã cập nhật.</P>
            </>
          ),
        },
      ]}
    />
  );
}